import React from 'react'
import { StyleSheet, View } from 'react-native'
import type { Suit, Value } from 'pokersolver'
import { DS } from '@/design-system'
import { PokerCard } from '@/components/GameAssets/Cards/PokerCard'

/** Mini board cards on the table card: small enough for five across. */
const BOARD_CARD_WIDTH = 26

/** Board positions 0-4 are fixed slots (flop/turn/river), never reordered —
 * safe to key on the position token rather than the card identity. */
const BOARD_SLOTS = ['board-0', 'board-1', 'board-2', 'board-3', 'board-4'] as const
export type TournamentTableCardProps = {
  tableNumber: number
  playerCount: number
  humanCount: number
  botCount: number
  averageStack: number
  largestStack: number
  smallestStack: number
  handInProgress: boolean
  /** Community cards on the board right now; empty preflop / between hands. */
  communityCards: string[]
  /** Pot piles + the current street's uncommitted bets; 0 between hands. */
  pot: number
  isMyCurrentTable: boolean
  showBotBreakdown: boolean
  /** When provided the card renders an Observe button; otherwise it's read-only. */
  onObserve?: () => void
  observeDisabled?: boolean
}

/**
 * Table summary card shared between the spectator (eliminated) and read-only
 * (still-in-tournament) variants. Previously these were two ~120-line copies in
 * `TournamentTablesScreen.tsx`.
 */
export function TournamentTableCard(props: TournamentTableCardProps) {
  const {
    tableNumber,
    playerCount,
    humanCount,
    botCount,
    averageStack,
    largestStack,
    smallestStack,
    handInProgress,
    communityCards,
    pot,
    isMyCurrentTable,
    showBotBreakdown,
    onObserve,
    observeDisabled
  } = props

  return (
    <View style={[styles.tableCard, isMyCurrentTable && styles.myTableCard, !onObserve && styles.readOnlyCard]}>
      <DS.Frame gap="8">
        <DS.Frame row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <DS.Frame row gap="8" style={{ alignItems: 'center' }}>
            <DS.Text variant="Heading 3/Medium">Table {tableNumber}</DS.Text>
            {isMyCurrentTable && (
              <View style={styles.myTableBadge}>
                <DS.Text variant="Mini/Bold" style={{ color: DS.palette['Brand/Maroon'] }}>
                  Your Table
                </DS.Text>
              </View>
            )}
          </DS.Frame>
          <View
            style={[
              styles.statusDot,
              { backgroundColor: handInProgress ? DS.palette['Utility/Success'] : DS.palette['Brand/Orange'] }
            ]}
            accessibilityLabel={handInProgress ? 'Hand in progress' : 'Idle'}
          />
        </DS.Frame>

        <DS.Frame row gap="16">
          <DS.Frame gap="4">
            <DS.Text variant="Mini/Bold" style={{ color: DS.palette['Brand/Dark Grey'] }}>
              Players
            </DS.Text>
            <DS.Text variant="Body Large/Medium">
              {playerCount}
              {showBotBreakdown && botCount > 0 ? ` (${humanCount}H / ${botCount}B)` : ''}
            </DS.Text>
          </DS.Frame>

          <DS.Frame gap="4">
            <DS.Text variant="Mini/Bold" style={{ color: DS.palette['Brand/Dark Grey'] }}>
              Avg Stack
            </DS.Text>
            <DS.Text variant="Body Large/Medium">{averageStack.toLocaleString()}</DS.Text>
          </DS.Frame>
        </DS.Frame>

        <DS.Frame row gap="16">
          <DS.Frame gap="4">
            <DS.Text variant="Mini/Bold" style={{ color: DS.palette['Brand/Dark Grey'] }}>
              Largest
            </DS.Text>
            <DS.Text variant="Body Large/Regular">{largestStack.toLocaleString()}</DS.Text>
          </DS.Frame>
          <DS.Frame gap="4">
            <DS.Text variant="Mini/Bold" style={{ color: DS.palette['Brand/Dark Grey'] }}>
              Smallest
            </DS.Text>
            <DS.Text variant="Body Large/Regular">{smallestStack.toLocaleString()}</DS.Text>
          </DS.Frame>
        </DS.Frame>

        {/* Always five slots so the row never appears/disappears between
            hands — undealt slots render as dashed placeholder outlines. */}
        <DS.Frame gap="6" style={styles.boardRow}>
          <DS.Frame row style={styles.boardCards}>
            {BOARD_SLOTS.map((slotKey, i) => {
              const card = communityCards[i]
              return card ? (
                <PokerCard.Front
                  key={slotKey}
                  value={card[0] as Value}
                  suit={card[1] as Suit}
                  size={BOARD_CARD_WIDTH}
                  shadow={false}
                />
              ) : (
                <PokerCard.Slot key={slotKey} size={BOARD_CARD_WIDTH} />
              )
            })}
          </DS.Frame>
          <DS.Text variant="Mini/Bold" style={{ color: DS.palette['Brand/Dark Grey'] }}>
            {/* The query reports pot: 0 between hands, so an unconditional
                label keeps the row stable without special-casing. */}
            {`Pot: ${pot.toLocaleString()}`}
          </DS.Text>
        </DS.Frame>

        {onObserve ? (
          <DS.Button
            type="On Light"
            variant="Medium/Primary"
            backgroundColor="Brand/Maroon"
            onPress={onObserve}
            disabled={observeDisabled}
          >
            Observe
          </DS.Button>
        ) : (
          <DS.Text variant="Mini/Regular" style={{ color: DS.palette['Brand/Dark Grey'] }}>
            Table overview only. Spectating unlocks after elimination.
          </DS.Text>
        )}
      </DS.Frame>
    </View>
  )
}

const styles = StyleSheet.create({
  tableCard: {
    backgroundColor: DS.palette['Global/White'],
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: DS.palette['Brand/Light Grey']
  },
  boardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  boardCards: {
    gap: 4,
    alignItems: 'center',
    minHeight: Math.round(26 * 1.47)
  },
  readOnlyCard: {
    opacity: 0.95
  },
  myTableCard: {
    borderColor: DS.palette['Brand/Maroon'],
    borderWidth: 2
  },
  myTableBadge: {
    backgroundColor: DS.palette['Brand/Cream'],
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3
  },
  statusDot: {
    width: 10,
    height: 10,
    borderRadius: 5
  }
})
